<script>
import Vue from 'vue'
// https://vue-multiselect.js.org/
import Multiselect from 'vue-multiselect'

export default {
    extends: Multiselect,
    name:'e-select',
    props: {
        placeholder: {
            type: String,
            default: () => {
                Vue.prototype.$t('select.placeholder')
            }
        },
        selectLabel: {
            type: String,
            default: () => {
                Vue.prototype.$t('select.selectLabel')
            }
        },
        selectGroupLabel: {
            type: String,
            default: () => {
                Vue.prototype.$t('select.selectGroupLabel')
            }
        },
        selectedLabel: {
            type: String,
            default: () => {
                Vue.prototype.$t('select.selectedLabel')
            }
        },
        deselectLabel: {
            type: String,
            default: () => {
                Vue.prototype.$t('select.deselectLabel')
            }
        },
        deselectGroupLabel: {
            type: String,
            default: () => {
                Vue.prototype.$t('select.deselectGroupLabel')
            }
        },
        sortable: {
            type: Boolean,
            default: true
        },
        sortDesc: {
            type: Boolean,
            default: false
        },
        optionsLimit: {
            type: Number,
            default: 300
        },
        internalSearch: {
            type: Boolean,
            default: true
        },
        groupLabel: {
            type: String,
            default: undefined
        },
        // En sélection multiple, la liste reste ouverte pour cocher plusieurs éléments à la suite
        closeOnSelect: {
            type: Boolean,
            default() {
                return !this.multiple
            }
        }
    },
    created() {
        if(this.sortable){
            this.sortOptions()
        }
    },
    render(h) {
        if (this.multiple && this.allSelectableOptions.length > 1 && !this.$slots.beforeList) {
            this.$slots.beforeList = [
                h('div', {
                    staticClass: 'multiselect__option multiselect__option--select-all',
                    class: { 'multiselect__option--selected': this.allOptionsSelected },
                    on: {
                        click: (e) => {
                            e.preventDefault()
                            this.toggleSelectAll()
                        }
                    }
                }, this.allOptionsSelected ? this.$t('select.deselect_all') : this.$t('select.select_all'))
            ]
        }
        const vnode = Multiselect.render.call(this, h)
        // Classe racine qui porte les cases à cocher de la sélection multiple (cf. _multiselect.scss)
        if (this.multiple) {
            vnode.data.staticClass += ' multiselect--multiple'
        }
        return vnode
    },
    methods: {
        /**
         * Surcharge de pointerMixin : classes d'un en-tête de groupe.
         * En sélection multiple, un groupe est toujours cochable, même avec :group-select="false".
         *
         * @param {Number} index Position de l'en-tête dans la liste filtrée
         * @param {Object} selectedGroup En-tête de groupe ($groupLabel)
         * @return {Array|String} Classes CSS de l'en-tête
         */
        groupHighlight(index, selectedGroup) {
            if (!this.groupSelect && !this.multiple) {
                return ['multiselect__option--group', 'multiselect__option--disabled']
            }

            const group = this.options.find(option => option[this.groupLabel] === selectedGroup.$groupLabel)
            if (!group || this.wholeGroupDisabled(group)) {
                return 'multiselect__option--disabled'
            }

            return [
                'multiselect__option--group',
                { 'multiselect__option--highlight': index === this.pointer && this.showPointer },
                { 'multiselect__option--group-selected': this.wholeGroupSelected(group) }
            ]
        },
        toggleSelectAll() {
            this.$emit('input', this.allOptionsSelected ? [] : this.allSelectableOptions.slice())
        },
        sortOptions() {
            if (this.options.length === 0) {
                return
            }

            const direction = this.sortDesc ? 'DESC' : 'ASC'
            if (!this.options.isSorted(this.trackBy, direction)) {
                this.options.sortBy(this.trackBy, direction)
            }
        },
        filterOptions (options, search, label, customLabel) {
            return options.filter(option => this.includes(customLabel(option, label), search))
        },
        flattenOptions (values, label) {
            return (options) =>
                options.reduce((prev, curr) => {
                /* istanbul ignore else */
                    if (curr[values] && curr[values].length) {
                        prev.push({
                            $groupLabel: curr[label],
                            $isLabel: true
                        })
                        return prev.concat(curr[values])
                    }
                    return prev
                }, [])
        },
        not (fun) {
            return (...params) => !fun(...params)
        },
        includes (str, query) {
            /* istanbul ignore else */
            if (str === undefined) str = 'undefined'
            if (str === null) str = 'null'
            if (str === false) str = 'false'
            const text = str.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            return text.indexOf(query.trim()) !== -1
        }
    },
    computed: {
        allSelectableOptions () {
            if (!this.groupValues) {
                return this.options
            }
            return this.options.reduce((acc, group) => acc.concat(group[this.groupValues] || []), [])
        },
        allOptionsSelected () {
            return this.allSelectableOptions.length > 0 && this.allSelectableOptions.every(this.isSelected)
        },
        filteredOptions () {
            let search = this.search || ''
            const normalizedSearch = search.toLowerCase().trim()
            const foldedSearch = normalizedSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            search = search.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

            let options = this.options.concat()

            /* istanbul ignore else */
            if (this.internalSearch) {
                options = this.groupValues
                ? this.filterAndFlat(options, normalizedSearch, this.label)
                : this.filterOptions(options, foldedSearch, this.label, this.customLabel)
            } else {
                options = this.groupValues ? this.flattenOptions(this.groupValues, this.groupLabel)(options) : options
            }

            options = this.hideSelected
            ? options.filter(this.not(this.isSelected))
            : options

            /* istanbul ignore else */
            if (this.taggable && normalizedSearch.length && !this.isExistingOption(normalizedSearch)) {
                if (this.tagPosition === 'bottom') {
                    options.push({ isTag: true, label: search })
                } else {
                    options.unshift({ isTag: true, label: search })
                }
            }

            return options.slice(0, this.optionsLimit)
        },
    },
    watch: {
        options(val) {
            if(this.sortable){
                this.sortOptions()
            }
        },
    }
}
</script>
